import {
  ChainID,
  BSC_CHAIN_NAME,
  TRON_CHAIN_NAME,
} from '@ankr.com/chains-list';

import { NetworkInfo } from './types';

export const TRON_REST_API_GROUP_ID = 'rest-api';

const AVALANCHE: NetworkInfo = {
  id: 43114,
  name: 'Avalanche',
  nativeCurrency: {
    name: 'Avalanche',
    symbol: 'AVAX',
    decimals: 18,
  },
  blockExplorers: {
    default: {
      name: 'Snowtrace',
      url: 'https://snowtrace.io/',
    },
  },
};

const AVALANCHE_FUJI: NetworkInfo = {
  id: 43113,
  name: 'Avalanche Fuji',
  nativeCurrency: {
    name: 'Avalanche',
    symbol: 'AVAX',
    decimals: 18,
  },
  blockExplorers: {
    default: {
      name: 'Snowtrace Testnet',
      url: 'https://testnet.snowtrace.io/',
    },
  },
  testnet: true,
};

const MANTLE: NetworkInfo = {
  id: 5000,
  name: 'Mantle Mainnet',
  nativeCurrency: {
    name: 'MNT',
    symbol: 'MNT',
    decimals: 18,
  },
  blockExplorers: {
    default: {
      name: 'Mantle Explorer',
      url: 'https://explorer.mantle.xyz',
    },
  },
};

const MANTLE_SEPOLIA: NetworkInfo = {
  id: 5003,
  name: 'Mantle Sepolia Testnet',
  nativeCurrency: {
    name: 'MNT',
    symbol: 'MNT',
    decimals: 18,
  },
  blockExplorers: {
    default: {
      name: 'Mantle Explorer Sepolia Testnet',
      url: 'https://explorer.sepolia.mantle.xyz/',
    },
  },
  testnet: true,
};

const ROLLUX: NetworkInfo = {
  id: 570,
  name: 'Rollux Mainnet',
  nativeCurrency: {
    name: 'SYS',
    symbol: 'SYS',
    decimals: 18,
  },
  blockExplorers: {
    default: {
      name: 'Rollux Explorer',
      url: 'https://explorer.rollux.com',
    },
  },
};

const ROLLUX_TESTNET: NetworkInfo = {
  id: 57000,
  name: 'Rollux Testnet',
  nativeCurrency: {
    name: 'TSYS',
    symbol: 'TSYS',
    decimals: 18,
  },
  blockExplorers: {
    default: {
      name: 'Rollux Tanenbaum Explorer',
      url: 'https://rollux.tanenbaum.io/',
    },
  },
  testnet: true,
};

const ZETACHAIN: NetworkInfo = {
  id: 7001,
  name: 'ZetaChain Athens Testnet',
  nativeCurrency: {
    name: 'ZETA',
    symbol: 'ZETA',
    decimals: 18,
  },
  blockExplorers: {
    default: {
      name: 'Zetachain Explorer',
      url: 'https://explorer.zetachain.com/',
    },
  },
};

const TRON_CHAIN_JSON_RPC: NetworkInfo = {
  id: 728126428,
  name: `${TRON_CHAIN_NAME} JSON-RPC`,
  nativeCurrency: {
    name: 'Ether',
    symbol: 'ETH',
    decimals: 18,
  },
  blockExplorers: {
    default: {
      name: 'Tron Explorer',
      url: 'https://tronscan.org/',
    },
  },
};

const ARBITRUM_NOVA: NetworkInfo = {
  id: 42170,
  name: 'Arbitrum Nova',
  nativeCurrency: {
    name: 'Ether',
    symbol: 'ETH',
    decimals: 18,
  },
  blockExplorers: {
    default: {
      name: 'Arbitrum Nova Explorer',
      url: 'https://nova-explorer.arbitrum.io',
    },
  },
};

const HORIZEN_MAINNET: NetworkInfo = {
  id: 7332,
  name: 'Horizen EON Mainnet',
  nativeCurrency: {
    name: 'Horizen',
    symbol: 'ZEN',
    decimals: 18,
  },
  blockExplorers: {
    default: {
      name: 'Horizen EON Mainnet Explorer',
      url: 'https://eon-explorer.horizenlabs.io',
    },
  },
};

const TENET_MAINNET: NetworkInfo = {
  id: 1559,
  name: 'Tenet Mainnet',
  nativeCurrency: {
    name: 'Tenet Mainnet',
    symbol: 'TENET',
    decimals: 18,
  },
  blockExplorers: {
    default: {
      name: 'Tenet Explorer',
      url: 'https://tenetscan.io/',
    },
  },
};

export const NETWORK_INFO_MAP: Partial<Record<ChainID, NetworkInfo>> = {
  // ? https://support.avax.network/en/articles/6077308-what-are-the-differences-between-the-x-p-and-c-chains
  [ChainID.AVALANCHE]: AVALANCHE,

  [ChainID.AVALANCHE_EVM]: AVALANCHE,

  [ChainID.AVALANCHE_FUJI]: AVALANCHE_FUJI,

  [ChainID.AVALANCHE_FUJI_EVM]: AVALANCHE_FUJI,

  [ChainID.ARBITRUM]: {
    id: 42161,
    name: 'Arbitrum',
    nativeCurrency: {
      name: 'Arbitrum One',
      symbol: 'ETH',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Arbiscan Explorer',
        url: 'https://arbiscan.io/',
      },
      arbitrum: {
        name: 'Arbitrum Explorer',
        url: 'https://explorer.arbitrum.io/',
      },
    },
  },

  [ChainID.ARBITRUM_SEPOLIA]: {
    id: 421614,
    name: 'Arbitrum Sepolia Testnet',
    nativeCurrency: {
      name: 'Arbitrum One',
      symbol: 'ETH',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Arbitrum Sepolia Explorer',
        url: 'https://sepolia.arbiscan.io/',
      },
    },
    testnet: true,
  },

  [ChainID.ARBITRUM_NOVA]: ARBITRUM_NOVA,

  [ChainID.ABCHAIN]: {
    id: 36888,
    name: 'Abchain',
    nativeCurrency: {
      name: 'AB',
      symbol: 'AB',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'AB Core Explorer',
        url: 'https://explorer.core.ab.org/',
      },
    },
  },

  [ChainID.ABCHAIN_TESTNET]: {
    id: 26888,
    name: 'Abchain testnet',
    nativeCurrency: {
      name: 'AB',
      symbol: 'AB',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'AB Core Testnet Explorer',
        url: 'https://explorer.core.testnet.ab.org/',
      },
    },
  },

  [ChainID.BSC]: {
    id: 56,
    name: BSC_CHAIN_NAME,
    nativeCurrency: {
      name: 'Smart Chain',
      symbol: 'BNB',
      decimals: 18,
    },
    searchKeys: ['bsc', 'binance'],
    blockExplorers: {
      default: {
        name: 'BNB Smart Chain Explorer',
        url: 'https://bscscan.com',
      },
    },
  },

  [ChainID.BSC_TESTNET_CHAPEL]: {
    id: 97, // * the only BSC testnet
    name: `${BSC_CHAIN_NAME} Testnet`,
    nativeCurrency: {
      name: 'Smart Chain - Testnet',
      symbol: 'BNB',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'BNB Smart Chain Testnet Explorer',
        url: 'https://testnet.bscscan.com',
      },
    },
    testnet: true,
  },

  [ChainID.BITLAYER_TESTNET]: {
    id: 200810,
    name: 'Bitlayer Chain Testnet',
    nativeCurrency: {
      name: 'BTC',
      symbol: 'BTC',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Bitlayer Chain Explorer',
        url: 'https://testnet-scan.bitlayer.org',
      },
    },
    testnet: true,
  },

  [ChainID.BOTANIX]: {
    id: 3637,
    name: 'Botanix',
    nativeCurrency: {
      name: 'BTC',
      symbol: 'BTC',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Botanix Explorer',
        url: 'https://rpc.btxtestchain.com',
      },
    },
  },

  [ChainID.BOTANIX_TESTNET]: {
    id: 3636,
    name: 'Botanix Testnet',
    nativeCurrency: {
      name: 'BTC',
      symbol: 'BTC',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Botanix Testnet Explorer',
        url: 'https://testnet.botanixscan.io',
      },
    },
    testnet: true,
  },

  [ChainID.BTTC]: {
    id: 199,
    name: 'BitTorrent Chain Mainnet',
    nativeCurrency: {
      name: 'BTTC Mainnet',
      symbol: 'BTT',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'BitTorrent Chain Explorer',
        url: 'https://testnet.botanixscan.io',
      },
    },
    testnet: true,
  },

  [ChainID.BLAST]: {
    id: 81457,
    name: 'Blast Mainnet',
    nativeCurrency: {
      name: 'BLAST',
      symbol: 'BLAST',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Blast Explorer',
        url: 'https://blastscan.io',
      },
    },
  },

  [ChainID.BLAST_TESTNET_SEPOLIA]: {
    id: 168587773,
    name: 'Blast s2 Testnet',
    nativeCurrency: {
      name: 'BLAST',
      symbol: 'BLAST',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Blast Testnet Sepolia Explorer',
        url: 'https://testnet.blastscan.io',
      },
    },
    testnet: true,
  },

  [ChainID.CELO]: {
    id: 42220,
    name: 'Celo (Mainnet)',
    nativeCurrency: {
      name: 'Celo (Mainnet)',
      symbol: 'CELO',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Celo Explorer',
        url: 'https://explorer.celo.org',
      },
    },
  },

  [ChainID.CELO_BAKLAVA]: {
    id: 62320,
    name: 'Celo Baklava Testnet',
    nativeCurrency: {
      name: 'Celo',
      symbol: 'CELO',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Celo Baklava Testnet Explorer',
        url: 'https://celo-baklava.blockscout.com/',
      },
    },
    testnet: true,
  },

  [ChainID.CELO_SEPOLIA]: {
    id: 11142220,
    name: 'Celo Sepolia Testnet',
    nativeCurrency: {
      name: 'Celo',
      symbol: 'CELO',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Celo Sepolia Testnet Explorer',
        url: 'https://celo-sepolia.blockscout.com/',
      },
    },
    testnet: true,
  },

  /* https://docs.chiliz.com/chiliz-chain-mainnet/connect-to-chiliz-chain/connect-using-rpc */
  [ChainID.CHILIZ]: {
    id: 88888,
    name: 'Chiliz Chain Mainnet',
    nativeCurrency: {
      name: 'Chiliz (Mainnet)',
      symbol: 'CHZ',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Chiliz Explorer',
        url: 'https://scan.chiliz.com/',
      },
    },
  },

  /* https://docs.chiliz.com/chiliz-chain-testnets/spicy-testnet/install-and-develop/install-and-use-metamask */
  [ChainID.CHILIZ_TESTNET]: {
    id: 88882,
    name: 'Chiliz Spicy Testnet',
    nativeCurrency: {
      name: 'Chiliz Spicy Testnet',
      symbol: 'CHZ',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Chiliz Spicy Testnet Explorer',
        url: 'http://spicy-explorer.chiliz.com/',
      },
    },
    testnet: true,
  },

  // https://chainlist.org/chain/1116?testnets=true
  [ChainID.CORE]: {
    id: 1116,
    name: 'Core Blockchain Mainnet',
    nativeCurrency: {
      name: 'Core Blockchain Mainnet',
      symbol: 'CORE',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Core Blockchain Mainnet Explorer',
        url: 'https://rpc-core.icecreamswap.com',
      },
    },
  },

  // * ChainID.ETH, ChainID.ETH_RINKEBY, ChainID.ETH_ROPSTEN
  // * Can't be added as they are default Metamask chains

  [ChainID.ETH_SEPOLIA]: {
    // ? ETH
    id: 11155111,
    name: 'Ethereum Sepolia',
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Ethereum Sepolia Explorer',
        url: 'https://sepolia.etherscan.io/',
      },
    },
    testnet: true,
  },

  [ChainID.ETH_HOODI]: {
    id: 560048,
    name: 'Ethereum Hoodi',
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Ethereum Hoodi Explorer',
        url: 'https://hoodi.etherscan.io/',
      },
    },
    testnet: true,
  },

  [ChainID.ETH_HOLESKY]: {
    id: 17000,
    name: 'Ethereum Holesky',
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Ethereum Holesky Explorer',
        url: 'https://ethereum-holesky.publicnode.com/',
      },
    },
    testnet: true,
  },

  [ChainID.ELECTRONEUM]: {
    id: 52014,
    name: 'Electroneum Mainnet',
    nativeCurrency: {
      name: 'ETN',
      symbol: 'ETN',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Electroneum Explorer',
        url: 'https://blockexplorer.electroneum.com/',
      },
    },
  },

  [ChainID.ELECTRONEUM_TESTNET]: {
    id: 5201420,
    name: 'Electroneum Testnet',
    nativeCurrency: {
      name: 'ETN',
      symbol: 'ETN',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Electroneum Testnet Explorer',
        url: 'https://blockexplorer.thesecurityteam.rocks/',
      },
    },
    testnet: true,
  },

  [ChainID.FANTOM]: {
    id: 250,
    name: 'Fantom Opera',
    nativeCurrency: {
      name: 'Fantom',
      symbol: 'FTM',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Fantom Opera Explorer',
        url: 'https://ftmscan.com/',
      },
    },
  },

  [ChainID.FANTOM_TESTNET]: {
    id: 4002,
    name: 'Fantom Testnet',
    nativeCurrency: {
      name: 'Fantom Testnet',
      symbol: 'FTM',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Fantom Testnet Explorer',
        url: 'https://testnet.ftmscan.com/',
      },
    },
    testnet: true,
  },

  [ChainID.FLARE]: {
    id: 14,
    name: 'Flare',
    nativeCurrency: {
      name: 'Flare',
      symbol: 'FLR',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Flare Explorer',
        url: 'https://flare-explorer.flare.network',
      },
    },
  },

  [ChainID.GNOSIS]: {
    id: 100, // * 0x64
    name: 'Gnosis Chain',
    nativeCurrency: {
      name: 'Gnosis',
      symbol: 'xDai',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Gnosis Chain Explorer',
        url: 'https://blockscout.com/xdai/mainnet/',
      },
    },
  },

  [ChainID.GNOSIS_TESTNET]: {
    id: 10200,
    name: 'Chiado Chain',
    nativeCurrency: {
      name: 'Chiado',
      symbol: 'Chiado',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Gnosis Chiado Testnet Explorer',
        url: 'https://blockscout.chiadochain.net/',
      },
    },
    testnet: true,
  },

  [ChainID.HORIZEN]: HORIZEN_MAINNET,

  [ChainID.HORIZEN_EVM]: HORIZEN_MAINNET,

  /* https://chainlist.org/?chain=1663&testnets=true&search=horizen */
  [ChainID.HORIZEN_TESTNET]: {
    id: 1663,
    name: 'Horizen Gobi Testnet',
    nativeCurrency: {
      name: 'Horizen',
      symbol: 'tZEN',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Horizen Gobi Testnet Explorer',
        url: 'https://gobi-testnet.horizenlabs.io/ethv1',
      },
    },
    testnet: true,
  },

  [ChainID.IOTEX]: {
    id: 4689,
    name: 'IoTeX Mainnet',
    nativeCurrency: {
      name: 'IoTeX Mainnet',
      symbol: 'IOTX',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'IoTeX Mainnet Explorer',
        url: 'https://iotexscan.io/',
      },
    },
  },

  [ChainID.IOTEX_TESTNET]: {
    id: 4690,
    name: 'IoTeX Testnet',
    nativeCurrency: {
      name: 'IoTeX Testnet',
      symbol: 'IOTX',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'IoTeX Testnet Explorer',
        url: 'https://testnet.iotexscan.io/',
      },
    },
    testnet: true,
  },

  [ChainID.METIS]: {
    id: 1088,
    name: 'Metis Andromeda Mainnet',
    nativeCurrency: {
      name: 'Metis Andromeda',
      symbol: 'METIS',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Metis Andromeda Mainnet Explorer',
        url: 'https://andromeda-explorer.metis.io/',
      },
    },
    testnet: true,
  },

  [ChainID.MOONBEAM]: {
    id: 1284,
    name: 'Moonbeam',
    nativeCurrency: {
      name: 'Glimmer',
      symbol: 'GLMR',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Moonbeam Explorer',
        url: 'https://moonbeam.moonscan.io/',
      },
    },
  },

  [ChainID.NERVOS_EVM]: {
    id: 71402, // * v1's id
    name: 'Godwoken/PolyJuice Mainnet',
    nativeCurrency: {
      name: 'CKByte',
      symbol: 'CKB',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Godwoken/PolyJuice Mainnet Explorer',
        url: 'https://gwscan.com/',
      },
    },
  },

  [ChainID.XLAYER]: {
    id: 196,
    name: 'X Layer',
    nativeCurrency: {
      name: 'OKB',
      symbol: 'OKB',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'X Layer Explorer',
        url: 'https://www.okx.com/explorer/xlayer',
      },
    },
  },

  [ChainID.XLAYER_TESTNET]: {
    id: 195,
    name: 'X Layer Testnet',
    nativeCurrency: {
      name: 'OKB',
      symbol: 'OKB',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'X Layer Explorer',
        url: 'https://www.okx.com/explorer/xlayer-test',
      },
    },
    testnet: true,
  },

  [ChainID.OPTIMISM]: {
    id: 10,
    name: 'Optimism',
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Optimism Explorer',
        url: 'https://optimistic.etherscan.io/',
      },
    },
  },

  [ChainID.OPTIMISM_SEPOLIA]: {
    id: 11155420,
    name: 'OP Sepolia',
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Optimism Sepolia Testnet Explorer',
        url: 'https://testnet-explorer.optimism.io/',
      },
    },
    testnet: true,
  },

  [ChainID.PLASMA]: {
    id: 9745,
    name: 'Plasma Mainnet',
    nativeCurrency: {
      name: 'Plasma',
      symbol: 'XPL',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Plasma Explorer',
        url: 'https://plasmascan.to',
      },
    },
  },

  [ChainID.POLYGON]: {
    id: 137,
    name: 'Polygon Mainnet',
    nativeCurrency: {
      name: 'POL',
      symbol: 'POL',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Polygon Mainnet Explorer',
        url: 'https://polygonscan.com/',
      },
    },
  },

  [ChainID.POLYGON_AMOY]: {
    id: 80002,
    name: 'Amoy Testnet',
    nativeCurrency: {
      name: 'POL',
      symbol: 'POL',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Amoy Testnet Explorer',
        url: 'https://www.oklink.com/amoy',
      },
    },
    testnet: true,
  },

  [ChainID.POLYGON_ZKEVM]: {
    id: 1101,
    name: 'Polygon zkEVM',
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Polygon zkEVM Explorer',
        url: 'https://zkevm.polygonscan.com/',
      },
    },
  },

  [ChainID.POLYGON_ZKEVM_TESTNET]: {
    id: 1442,
    name: 'Polygon zkEVM Testnet',
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Polygon zkEVM Testnet Explorer',
        url: 'https://testnet-zkevm.polygonscan.com/',
      },
    },
    testnet: true,
  },

  [ChainID.POLYGON_ZKEVM_CARDONA]: {
    id: 2442,
    name: 'Polygon zkEVM Cardona Testnet',
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Polygon zkEVM Cardona Testnet Explorer',
        url: 'https://cardona-zkevm.polygonscan.com/',
      },
    },
    testnet: true,
  },

  [ChainID.SYSCOIN]: {
    id: 57,
    name: 'Syscoin Mainnet',
    nativeCurrency: {
      name: 'Syscoin',
      symbol: 'SYS',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Syscoin Explorer',
        url: 'https://explorer.syscoin.org/',
      },
    },
  },

  [ChainID.KITE]: {
    id: 2366,
    name: 'KiteAI Mainnet',
    nativeCurrency: {
      name: 'KITE',
      symbol: 'KITE',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'KiteAI Mainnet Explorer',
        url: 'https://kitescan.ai/',
      },
    },
  },

  [ChainID.KINTO]: {
    id: 7887,
    name: 'Kinto Mainnet',
    nativeCurrency: {
      name: 'ETH',
      symbol: 'ETH',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Kinto Explorer',
        url: 'https://explorer.kinto.xyz/',
      },
    },
  },

  [ChainID.KAIA]: {
    id: 8217,
    name: 'Kaia Mainnet',
    nativeCurrency: {
      name: 'KAIA',
      symbol: 'KAIA',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Kaia Explorer',
        url: 'https://kaiascan.io/',
      },
    },
  },

  [ChainID.KAIA_TESTNET]: {
    id: 1001,
    name: 'Kaia Kairos Testnet',
    nativeCurrency: {
      name: 'KAIA',
      symbol: 'KAIA',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Kaia Kairos Testnet Explorer',
        url: 'https://kairos.kaiascan.io/',
      },
    },
    testnet: true,
  },

  [ChainID.HECO]: {
    id: 128,
    name: 'Heco',
    nativeCurrency: {
      name: 'HT',
      symbol: 'HT',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Heco Explorer',
        url: 'https://hecoinfo.com/',
      },
    },
  },

  [ChainID.HECO_TESTNET]: {
    id: 256,
    name: 'HECO Testnet',
    nativeCurrency: {
      name: 'HT',
      symbol: 'HT',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Heco Testnet Explorer',
        url: 'https://testnet.hecoinfo.com/',
      },
    },
    testnet: true,
  },

  [ChainID.HYPEREVM]: {
    id: 999,
    name: 'HyperEVM',
    nativeCurrency: {
      name: 'HYPE',
      symbol: 'HYPE',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'HyperEVM Explorer',
        url: 'https://hyperevmscan.io/',
      },
    },
  },

  [ChainID.TENET]: TENET_MAINNET,

  [ChainID.TENET_EVM]: TENET_MAINNET,

  // https://era.zksync.io/docs/dev/fundamentals/interacting.html#connecting-to-zksync-era-on-metamask
  [ChainID.ZKSYNC_ERA]: {
    id: 324,
    name: 'zkSync Era Mainnet',
    nativeCurrency: {
      name: 'ETH',
      symbol: 'ETH',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'zkSync Era Explorer',
        url: 'https://explorer.zksync.io/',
      },
    },
  },

  [ChainID.ZKSYNC_ERA_SEPOLIA]: {
    id: 300,
    name: 'zkSync Era Sepolia Testnet',
    nativeCurrency: {
      name: 'ETH',
      symbol: 'ETH',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'zkSync Era Sepolia Testnet Explorer',
        url: 'https://sepolia.explorer.zksync.io/',
      },
    },
    testnet: true,
  },

  [ChainID.LINEA]: {
    id: 59144,
    name: 'Linea Mainnet',
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Linea Explorer',
        url: 'https://lineascan.build',
      },
    },
  },

  [ChainID.MANTLE]: MANTLE,
  [ChainID.MANTLE_SEPOLIA]: MANTLE_SEPOLIA,

  [ChainID.ROLLUX]: ROLLUX,

  [ChainID.ROLLUX_TESTNET]: ROLLUX_TESTNET,

  [ChainID.ZETACHAIN]: ZETACHAIN,

  [ChainID.ZETACHAIN_EVM_ATHENS_TESTNET]: ZETACHAIN,

  [ChainID.TRON]: undefined,

  [ChainID.TRON_JSON_RPC]: TRON_CHAIN_JSON_RPC,

  [ChainID.BASE]: {
    id: 8453,
    name: 'Base Mainnet',
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Base Explorer',
        url: 'https://basescan.org',
      },
    },
  },

  [ChainID.BASE_SEPOLIA]: {
    id: 84532,
    name: 'Base Sepolia',
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Base Sepolia Explorer',
        url: 'https://sepolia-explorer.base.org/',
      },
    },
    testnet: true,
  },

  [ChainID.TAIKO]: {
    id: 167000,
    name: 'Taiko Mainnet',
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Taiko Explorer',
        url: 'https://taikoscan.io',
      },
    },
  },

  [ChainID.TAIKO_HOODI]: {
    id: 167012,
    name: 'Taiko Hoodi Testnet',
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Taiko Hoodi Testnet Explorer',
        url: 'https://hoodi.taikoscan.io/',
      },
    },
    testnet: true,
  },

  [ChainID.TELOS]: {
    id: 40,
    name: 'Telos',
    nativeCurrency: {
      name: 'TLOS',
      symbol: 'TLOS',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Telos Explorer',
        url: 'https://teloscan.io',
      },
    },
  },

  [ChainID.REDBELLY]: {
    id: 151,
    name: 'Redbelly Network Mainnet',
    nativeCurrency: {
      name: 'RBNT',
      symbol: 'RBNT',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Redbelly Explorer',
        url: 'https://redbelly.routescan.io',
      },
    },
  },

  [ChainID.ROBINHOOD]: {
    id: 4663,
    name: 'Robinhood Chain Mainnet',
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Robinhood Chain Explorer',
        url: 'https://robinhoodchain.blockscout.com',
      },
    },
  },

  [ChainID.SCROLL]: {
    id: 534352,
    name: 'Scroll Mainnet',
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Scroll Explorer',
        url: 'https://blockscout.scroll.io',
      },
    },
  },

  [ChainID.SCROLL_TESTNET]: {
    id: 534353,
    name: 'Scroll Alpha Testnet',
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Scroll Alpha Testnet Explorer',
        url: 'https://alpha-blockscout.scroll.io',
      },
    },
    testnet: true,
  },

  [ChainID.SCROLL_SEPOLIA_TESTNET]: {
    id: 534351,
    name: 'Scroll Sepolia Testnet',
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Scroll Sepolia Testnet Explorer',
        url: 'https://sepolia-blockscout.scroll.io/',
      },
    },
    testnet: true,
  },

  [ChainID.UNICHAIN]: {
    id: 130,
    name: 'Unichain',
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Unichain Explorer',
        url: 'https://uniscan.xyz/',
      },
    },
  },

  [ChainID.XDC]: {
    id: 50,
    name: 'XDC Network',
    nativeCurrency: {
      name: 'XDC',
      symbol: 'XDC',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'XDC Network Explorer',
        url: 'https://xdcscan.io',
      },
    },
  },

  [ChainID.XDC_TESTNET]: {
    id: 51,
    name: 'XDC Testnet',
    nativeCurrency: {
      name: 'TXDC',
      symbol: 'TXDC',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'XDC Testnet Explorer',
        url: 'https://explorer.apothem.network/',
      },
    },
    testnet: true,
  },

  [ChainID.KAVA_EVM]: {
    id: 2222,
    name: 'Kava',
    nativeCurrency: {
      name: 'KAVA',
      symbol: 'KAVA',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Kava Explorer',
        url: 'https://kavascan.com/',
      },
    },
  },

  [ChainID.KAVA_EVM_TESTNET]: {
    id: 2221,
    name: 'Kava Testnet',
    nativeCurrency: {
      name: 'KAVA',
      symbol: 'KAVA',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Kava Testnet Explorer',
        url: 'https://testnet.kavascan.com/',
      },
    },
    testnet: true,
  },

  [ChainID.XAI]: {
    id: 660279,
    name: 'XAI',
    nativeCurrency: {
      name: 'XAI',
      symbol: 'XAI',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'XAI Explorer',
        url: 'https://explorer.xai-chain.net/',
      },
    },
  },

  [ChainID.XAI_TESTNET]: {
    id: 47279324479,
    name: 'XAI Testnet',
    nativeCurrency: {
      name: 'GETH',
      symbol: 'GETH',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'XAI Testnet Explorer',
        url: 'https://testnet-explorer.xai-chain.net/',
      },
    },
    testnet: true,
  },

  [ChainID.BAHAMUT]: {
    id: 5165,
    name: 'Bahamut',
    nativeCurrency: {
      name: 'FTN',
      symbol: 'FTN',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Bahamut Explorer',
        url: 'https://www.ftnscan.com/',
      },
    },
  },

  [ChainID.BAHAMUT_OCEAN]: {
    id: 4058,
    name: 'Bahamut Ocean Testnet',
    nativeCurrency: {
      name: 'FTN',
      symbol: 'FTN',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Bahamut Ocean Testnet Explorer',
        url: 'https://ocean.ftnscan.com/',
      },
    },
    testnet: true,
  },

  [ChainID.ZERO_G_EVM]: {
    id: 16661,
    name: '0G Mainnet',
    nativeCurrency: {
      name: '0G',
      symbol: '0G',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: '0G GMainnet Explorer',
        url: 'https://chainscan.0g.ai',
      },
    },
  },

  [ChainID.ZERO_G_GALILEO_EVM]: {
    id: 16601,
    name: '0G Galileo Testnet',
    nativeCurrency: {
      name: 'Neuron',
      symbol: 'A0GI',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: '0G Galileo Testnet Explorer',
        url: 'https://chainscan-galileo.0g.ai/',
      },
    },
    testnet: true,
  },

  [ChainID.ATLETA]: {
    id: 2440,
    name: 'Atleta Mainnet',
    nativeCurrency: {
      name: 'ATLA',
      symbol: 'ATLA',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Atleta Explorer',
        url: 'https://blockscout.atleta.network/',
      },
    },
  },

  [ChainID.ATLETA_OLYMPIA]: {
    id: 2340,
    name: 'Atleta Olympia',
    nativeCurrency: {
      name: 'ATLA',
      symbol: 'ATLA',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Atleta Explorer',
        url: 'https://blockscout.atleta.network/',
      },
    },
  },

  [ChainID.STORY]: {
    id: 1514,
    name: 'Story Mainnet',
    nativeCurrency: {
      name: 'IP',
      symbol: 'IP',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Story Explorer',
        url: 'https://storyscan.xyz',
      },
    },
  },

  [ChainID.STORY_ODYSSEY]: {
    id: 1516,
    name: 'Story Odyssey Testnet',
    nativeCurrency: {
      name: 'IP',
      symbol: 'IP',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Story Odyssey Testnet Explorer',
        url: 'https://testnet.storyscan.xyz/',
      },
    },
    testnet: true,
  },

  [ChainID.STORY_AENEID]: {
    id: 1315,
    name: 'Story Aeneid Testnet',
    nativeCurrency: {
      name: 'IP',
      symbol: 'IP',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Story Aeneid Testnet Explorer',
        url: 'https://aeneid.storyscan.xyz',
      },
    },
    testnet: true,
  },

  [ChainID.SWELL]: {
    id: 1923,
    name: 'Swell Mainnet',
    nativeCurrency: {
      name: 'SWELL',
      symbol: 'SWELL',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Swell Explorer',
        url: 'https://explorer.swellnetwork.io/',
      },
    },
  },

  [ChainID.SWELL_SEPOLIA]: {
    id: 1924,
    name: 'Swell Sepolia Testnet',
    nativeCurrency: {
      name: 'SWELL',
      symbol: 'SWELL',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Swell Sepolia Testnet Explorer',
        url: 'https://swell-testnet-explorer.alt.technology/',
      },
    },
    testnet: true,
  },

  [ChainID.TAC_EVM]: {
    id: 239,
    name: 'TAC',
    nativeCurrency: {
      name: 'TAC',
      symbol: 'TAC',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'TAC Explorer',
        url: 'https://evm.explorer.tac.build/',
      },
    },
  },

  [ChainID.GOAT_TESTNET]: {
    id: 48816,
    name: 'Goat Testnet',
    nativeCurrency: {
      name: 'BTC',
      symbol: 'BTC',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Goat Testnet Explorer',
        url: 'https://explorer.testnet3.goat.network',
      },
    },
    testnet: true,
  },

  [ChainID.GOAT]: {
    id: 2345,
    name: 'Goat Mainnet',
    nativeCurrency: {
      name: 'BTC',
      symbol: 'BTC',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Goat Explorer',
        url: 'https://explorer.goat.network/',
      },
    },
  },

  [ChainID.CORN_MAIZENET]: {
    id: 21000000,
    name: 'Corn Maizenet',
    nativeCurrency: {
      name: 'BTCN',
      symbol: 'BTCN',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Corn Maizenet Explorer',
        url: 'https://maizenet-explorer.usecorn.com/',
      },
    },
  },

  [ChainID.CORN_TESTNET]: {
    id: 21000001,
    name: 'Corn Testnet',
    nativeCurrency: {
      name: 'BTCN',
      symbol: 'BTCN',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Corn Testnet Explorer',
        url: 'https://testnet-explorer.usecorn.com/',
      },
    },
    testnet: true,
  },

  [ChainID.SONIC_BLAZE_TESTNET]: {
    id: 57054,
    name: 'Sonic Blaze Testnet',
    nativeCurrency: {
      name: 'S',
      symbol: 'S',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Sonic Blaze Testnet Explorer',
        url: 'https://explorer.blaze.soniclabs.com/',
      },
    },
    testnet: true,
  },

  [ChainID.MATCHAIN]: {
    id: 698,
    name: 'Matchain Mainnet',
    nativeCurrency: {
      name: 'BNB',
      symbol: 'BNB',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Matchain Explorer',
        url: 'https://matchscan.io',
      },
    },
  },

  [ChainID.IOTA_EVM]: {
    id: 8822,
    name: 'IOTA EVM Mainnet',
    nativeCurrency: {
      name: 'IOTA',
      symbol: 'IOTA',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'IOTA EVM Mainnet Explorer',
        url: 'https://explorer.evm.iota.org/',
      },
    },
  },

  [ChainID.IOTA_EVM_TESTNET]: {
    id: 1075,
    name: 'IOTA EVM Testnet',
    nativeCurrency: {
      name: 'IOTA',
      symbol: 'IOTA',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'IOTA EVM Explorer',
        url: 'https://explorer.evm.testnet.iotaledger.net/',
      },
    },
    testnet: true,
  },

  [ChainID.SOMNIA]: {
    id: 5031,
    name: 'Somnia Mainent',
    nativeCurrency: {
      name: 'SOMI',
      symbol: 'SOMI',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Somnia Mainent Explorer',
        url: 'https://explorer.somnia.network/txs',
      },
    },
  },

  [ChainID.SOMNIA_TESTNET]: {
    id: 50312,
    name: 'Somnia Testnet',
    nativeCurrency: {
      name: 'STT',
      symbol: 'STT',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Somnia Testnet Explorer',
        url: 'https://somnia-devnet.socialscan.io/',
      },
    },
    testnet: true,
  },

  [ChainID.WHITECHAIN]: {
    id: 1875,
    name: 'Whitechain',
    nativeCurrency: {
      name: 'WhiteBIT Coin',
      symbol: 'WBT',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Whitechain Explorer',
        url: 'https://explorer.whitechain.io',
      },
    },
  },

  [ChainID.WHITECHAIN_TESTNET]: {
    id: 1874,
    // Official name of 1874; 'Whitechain Testnet' is the legacy chain 2625.
    name: 'Whitechain Sepolia',
    nativeCurrency: {
      name: 'WhiteBIT Coin',
      symbol: 'WBT',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Whitechain Sepolia Explorer',
        url: 'https://explorer.testnet.whitechain.io',
      },
    },
    testnet: true,
  },

  [ChainID.WORLDCHAIN]: {
    id: 480,
    name: 'World Chain',
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'World Chain Explorer',
        url: 'https://worldscan.org',
      },
    },
  },

  [ChainID.TAC_TESTNET_SPB]: {
    id: 2391,
    name: 'TAC SPB Testnet',
    nativeCurrency: {
      name: 'TAC',
      symbol: 'TAC',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'TAC SPB Explorer',
        url: 'https://spb.explorer.tac.build/',
      },
    },
    testnet: true,
  },

  [ChainID.SEI_EVM]: {
    id: 1329,
    name: 'SEI Mainnet',
    nativeCurrency: {
      name: 'SEI',
      symbol: 'SEI',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Sei Explorer',
        url: 'https://seitrace.com',
      },
    },
  },

  [ChainID.ETHERLINK]: {
    id: 42793,
    name: 'Etherlink Mainnet',
    nativeCurrency: {
      name: 'XTZ',
      symbol: 'XTZ',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Etherlink Explorer',
        url: 'https://explorer.etherlink.com/',
      },
    },
  },

  [ChainID.ETHERLINK_TESTNET]: {
    id: 128123,
    name: 'Etherlink Testnet',
    nativeCurrency: {
      name: 'XTZ',
      symbol: 'XTZ',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Etherlink Testnet Explorer',
        url: 'https://testnet.explorer.etherlink.com/',
      },
    },
    testnet: true,
  },

  [ChainID.ETHERLINK_SHADOWNET_TESTNET]: {
    id: 127823,
    name: 'Etherlink Shadownet Testnet',
    nativeCurrency: {
      name: 'XTZ',
      symbol: 'XTZ',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Etherlink Shadownet Testnet Explorer',
        url: 'https://shadownet.explorer.etherlink.com',
      },
    },
    testnet: true,
  },

  [ChainID.MONAD]: {
    id: 143,
    name: 'Monad',
    nativeCurrency: {
      name: 'MON',
      symbol: 'MON',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Monad Explorer',
        url: 'https://monadexplorer.com/',
      },
    },
  },
  [ChainID.MONAD_TESTNET]: {
    id: 10143,
    name: 'Monad Testnet',
    nativeCurrency: {
      name: 'MON',
      symbol: 'MON',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Monad Testnet Explorer',
        url: 'https://testnet.monadexplorer.com/',
      },
    },
    testnet: true,
  },

  [ChainID.XPHERE]: {
    id: 20250217,
    name: 'Xphere Mainnet',
    nativeCurrency: {
      name: 'XP',
      symbol: 'XP',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Xphere Explorer',
        url: 'https://xp.tamsa.io/',
      },
    },
  },

  [ChainID.XPHERE_TESTNET]: {
    id: 1998991,
    name: 'Xphere Testnet',
    nativeCurrency: {
      name: 'XP',
      symbol: 'XP',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Xphere Testnet Explorer',
        url: 'https://xpt.tamsa.io/',
      },
    },
    testnet: true,
  },

  [ChainID.ZKVERIFY_VFLOW_PARACHAIN_TESTNET]: {
    id: 1409,
    name: 'vFlow Parachain Testnet',
    nativeCurrency: {
      name: 'tVFY',
      symbol: 'tVFY',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'vFlow Parachain Testnet Explorer',
        url: 'https://vflow-testnet.subscan.io/',
      },
    },
    testnet: true,
  },

  [ChainID.ZKVERIFY_VFLOW_PARACHAIN_MAINNET]: {
    id: 1408,
    name: 'vFlow Mainnet',
    nativeCurrency: {
      name: 'VFY',
      symbol: 'VFY',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'vFlow Mainnet Explorer',
        url: 'https://zkverify.subscan.io',
      },
    },
  },

  [ChainID.DOGEOS_TESTNET]: {
    id: 568,
    name: 'DogeOS Testnet',
    nativeCurrency: {
      name: 'DOGE',
      symbol: 'DOGE',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'DogeOS Testnet Explorer',
        url: 'https://blockscout.testnet.dogeos.com/',
      },
    },
  },

  [ChainID.STATUS_TESTNET]: {
    id: 1660990954,
    name: 'Status Network Testnet',
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    blockExplorers: {
      default: {
        name: 'Status Network Testnet Explorer',
        url: 'https://sepoliascan.status.network',
      },
    },
    testnet: true,
  },
};
